import React, { createContext, useCallback, useState } from 'react';

import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';

import Html from '../components/Html';

/**
 * @typedef ModalProps
 * @type {object}
 * @property {null|string|Element} title
 * @property {null|string|Element} content
 * @property {null|string} confirmButton
 * @property {null|string} cancelButton
 * @property {null|function} onConfirm
 * @property {null|function} onCancel
 */

/**
 * @typedef ModalShowFn
 * @type {function}
 * @param {ModalProps} props
 */

/**
 * @typedef ModalContextProps
 * @type {object}
 * @property {function} close
 * @property {ModalShowFn} alert
 * @property {ModalShowFn} confirm
 */

/**
 * @type {React.Context<null|ModalContextProps>}
 */
const ModalContext = createContext(null);

const defaultProps = {
  type: 'alert',
  confirmButton: 'OK',
  cancelButton: 'Cancelar',
};

function ModalProvider({ children }) {
  const [open, setOpen] = useState(false);

  /** @type {ModalProps} props */
  const [props, setProps] = useState(defaultProps);

  let { type, title, content, confirmButton, cancelButton, onConfirm, onCancel, ...dialogProps } = props;

  const close = useCallback(
    (e) => {
      setOpen(false);
      typeof onCancel === 'function' && onCancel(e);
    },
    [onCancel],
  );

  /** @type {ModalShowFn} */
  const show = useCallback(
    (newProps) => {
      setOpen(true);
      setProps({ ...defaultProps, onConfirm: close, onCancel: close, ...newProps });
    },
    [close],
  );

  /** @type {ModalShowFn} */
  const alert = useCallback(
    (dialogProps) => {
      if (typeof dialogProps === 'string') {
        dialogProps = { content: dialogProps };
      }

      show({ ...dialogProps, type: 'alert' });
    },
    [show],
  );

  /** @type {ModalShowFn} */
  const confirm = useCallback(
    (dialogProps) => {
      if (typeof dialogProps === 'string') {
        dialogProps = { content: dialogProps };
      }

      show({ ...dialogProps, type: 'confirm' });
    },
    [show],
  );

  if (typeof content === 'string') {
    content = <Html html={content} />;
  }

  return (
    <ModalContext.Provider
      value={{
        close,
        alert,
        confirm,
      }}
    >
      {children}
      <Dialog keepMounted {...dialogProps} open={open}>
        <DialogTitle disableTypography>
          <Grid container spacing={2} justifyContent="space-between" wrap="nowrap">
            <Grid item>
              <Box component="h1" m={0} fontSize={18}>
                {title || ''}
              </Box>
            </Grid>
            <Grid item>
              <Box mt={-1} mr={-2}>
                <IconButton size="small" aria-label="close" onClick={type === 'alert' ? onConfirm : onCancel}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </DialogTitle>
        {Boolean(content) && <DialogContent>{content}</DialogContent>}
        <DialogActions>
          {type === 'confirm' && (
            <Button onClick={onCancel} color="primary">
              {cancelButton}
            </Button>
          )}
          <Button onClick={onConfirm} color="primary">
            {confirmButton}
          </Button>
        </DialogActions>
      </Dialog>
    </ModalContext.Provider>
  );
}

export { ModalProvider };

export default ModalContext;
